import React, { useMemo } from 'react';
import { Eye, ChevronLeft, Database, MessageSquare, CheckCircle2, XCircle, Image, BookOpen, ShieldCheck, Tag } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { type Question, getQuestionWithExplanation } from '../../../../content';
import { QuestionCardClean } from '../../../../practice/components/QuestionCardClean';
import { QuestionRendererFactory } from '../../../../practice/components/renderers/QuestionRendererFactory';
import { VisualAidFactory } from '../../../../visuals/components/VisualAidFactory';
interface QuestionPreviewPanelProps {
  questionId: string | null;
  onBack?: () => void;
  accentColor?: 'blue' | 'purple' | 'emerald' | 'amber';
  showBackOnMobile?: boolean;
  onNavigateToGovernance?: (questionId: string) => void;
}
const ACCENT_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-l-blue-500',
    text: 'text-blue-600'
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-l-purple-500',
    text: 'text-purple-600'
  },
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-l-emerald-500',
    text: 'text-emerald-600'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-l-amber-500',
    text: 'text-amber-600'
  }
};
export function QuestionPreviewPanel({
  questionId,
  onBack,
  accentColor = 'blue',
  showBackOnMobile = true,
  onNavigateToGovernance
}: QuestionPreviewPanelProps) {
  const question = useMemo(() => {
    if (!questionId) return null;
    return getQuestionWithExplanation(questionId);
  }, [questionId]);
  const colors = ACCENT_COLORS[accentColor];
  const visualCount = useMemo(() => {
    if (!question) return 0;
    return (question.stimulusAids?.length || 0) + (question.supportAids?.length || 0);
  }, [question]);
  // Tier color helper
  const getTierColor = (tier?: number): 'neutral' | 'success' | 'info' | 'warning' => {
    if (!tier) return 'neutral';
    if (tier <= 3) return 'success';
    if (tier <= 6) return 'info';
    return 'warning';
  };
  // Empty state
  if (!question) {
    return <div className="flex-1 flex items-center justify-center text-gray-400 p-6 bg-slate-50" data-id="element-6206" data-mp="u3n4c1glqq0y">
        <div className="text-center" data-id="element-6207" data-mp="y3ntag1bujm9">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-6208" data-mp="12wgx91oz1nxa">
            <Eye className="w-8 h-8 text-gray-300" data-id="element-6209" data-mp="17vtgzdkjqevn" />
          </div>
          <p className="font-semibold text-gray-500" data-id="element-6210" data-mp="17x4n3k1grypx">Selecteer een vraag</p>
          <p className="text-sm mt-1 text-gray-400" data-id="element-6211" data-mp="b6u93znvjf3z">
            Kies een vraag uit de lijst om de preview te bekijken
          </p>
        </div>
      </div>;
  }
  return <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50" style={{
    WebkitOverflowScrolling: 'touch'
  }} data-id="element-6212" data-mp="k5fxwcjxvqd7">
      
      <div className="p-4 sm:p-6 md:p-8 pb-8 sm:pb-12" data-id="element-6213" data-mp="p6dggq8v2mf8">
        <div className="max-w-3xl mx-auto" data-id="element-6214" data-mp="1vl4k31pdclcy">
          {/* Mobile back button */}
          {showBackOnMobile && onBack && <div className="mb-4 lg:hidden" data-id="element-6215" data-mp="20j671xpx14tp">
              <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-6216" data-mp="20m247jtxvph3">
              
                <ChevronLeft className="w-4 h-4" data-id="element-6217" data-mp="3bc6ve2p6my3l" />
                Terug naar lijst
              </button>
            </div>}

          {/* Question header badges */}
          <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-6218" data-mp="36ctxmmwrw6m8">
            <Badge variant="info" size="sm" data-id="element-6219" data-mp="32cjt7eompo9h">
              {question.questionType}
            </Badge>
            <span className="text-xs text-gray-500 font-mono truncate max-w-[200px]" data-id="element-6220" data-mp="2xbdxd7zo9kvq">
              {question.familyId}
            </span>
            {visualCount > 0 && <div className="flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-md border border-purple-100" data-id="element-6221" data-mp="2ne9bl99qnrfg">
                <Image className="w-3.5 h-3.5" data-id="element-6222" data-mp="qko762mhedz7" />
                {visualCount} visual{visualCount > 1 ? 's' : ''}
              </div>}
          </div>

          {/* Question Card */}
          <QuestionCardClean question={question} data-id="element-6223" data-mp="tolzpxlq1gha">
            <QuestionRendererFactory question={question} onAnswer={() => {}} data-id="element-6224" data-mp="tlq26adscttd" />
          </QuestionCardClean>

          {onNavigateToGovernance && <div className="mt-4 flex justify-end" data-id="element-6225" data-mp="jpwje99h4435">
              <button type="button" onClick={() => onNavigateToGovernance(question.id)} className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" data-id="element-6226" data-mp="jrhbztc89cyd">
              
                <ShieldCheck className="w-4 h-4" data-id="element-6227" data-mp="1080jeiwce45" />
                Bekijk validatie
              </button>
            </div>}

          {/* Metadata & Feedback Section */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6228" data-mp="3o330a10w6qcg">
            {/* Metadata Grid */}
            <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-6229" data-mp="3s3wrb39msl9k">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6230" data-mp="39ge3i6y0yr9r">
                <Database className="w-4 h-4 text-gray-400" data-id="element-6231" data-mp="34ickfm0lmrya" />
                Vraag Metadata
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-id="element-6232" data-mp="3ihudidkxgrdg">
                <div data-id="element-6233" data-mp="3dir8gt4y6l5h">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6234" data-mp="3di7ypzpgb32j">
                    Skill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6235" data-mp="3seguuse1p0aq">
                    {question.skill || '—'}
                  </span>
                </div>
                <div data-id="element-6236" data-mp="52fytqj93bkq">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6237" data-mp="52xrkd6pf7nw">
                    SubSkill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6238" data-mp="11maiw1wburb">
                    {question.subSkill || '—'}
                  </span>
                </div>
                <div data-id="element-6239" data-mp="a20u9qz7p1sp">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6240" data-mp="a2twcen8734i">
                    Level Code
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6241" data-mp="33vbmg5quajzt">
                    {question.levelCode || '—'}
                  </span>
                </div>
                <div data-id="element-6242" data-mp="3ctx3fwgnxqyh">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6243" data-mp="3ctnbw4fyzbqo">
                    Groep / Tier
                  </span>
                  <div className="flex items-center gap-2" data-id="element-6244" data-mp="3hn8ds7koopv5">
                    <span className="font-medium text-gray-800" data-id="element-6245" data-mp="3mj6wpfn8tfo6">
                      Groep {question.group || '?'}
                    </span>
                    <Badge variant={getTierColor(question.tier)} size="sm" data-id="element-6246" data-mp="3rlfr8gfrkpse">
                      Tier {question.tier || '?'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Pedagogy Section */}
            {(question.pedagogy?.dok || question.pedagogy?.misconceptionTags?.length) && <div className="p-4 sm:p-5 border-b border-gray-100 bg-slate-50/50" data-id="element-6247" data-mp="26571ghzcee79">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-6248" data-mp="1ymze1o9e4mep">
                  <BookOpen className="w-4 h-4 text-indigo-400" data-id="element-6249" data-mp="1tw0rw81q0u3j" />
                  Pedagogisch Contract
                </h4>
                <div className="flex flex-wrap gap-4" data-id="element-6250" data-mp="1oxpjm1r2qrnf">
                  {question.pedagogy?.dok && <div data-id="element-6251" data-mp="1jy34tnvll3ci">
                      <span className="text-xs text-gray-500 block mb-1.5" data-id="element-6252" data-mp="1jyw3s2obeok3">
                        DOK Niveau
                      </span>
                      <Badge variant="neutral" className="bg-indigo-50 text-indigo-700 border-indigo-200" data-id="element-6253" data-mp="2hiz7z4lxi00x">
                    
                        DOK {question.pedagogy.dok}
                      </Badge>
                    </div>}

                  {question.pedagogy?.misconceptionTags && question.pedagogy.misconceptionTags.length > 0 && <div data-id="element-6254" data-mp="w8icwfhguvr3">
                        <span className="text-xs text-gray-500 block mb-1.5 flex items-center gap-1" data-id="element-6255" data-mp="w8s1bymv4uuo">
                          <Tag className="w-3 h-3" data-id="element-6256" data-mp="s5lwmvfefbqb" /> Misconcepties
                        </span>
                        <div className="flex flex-wrap gap-1.5" data-id="element-6257" data-mp="2radqxjg2resd">
                          {question.pedagogy.misconceptionTags.map(tag => <Badge key={tag.id} variant="warning" size="sm" data-id="element-6258" data-mp="2wbjj9g5tp01d">
                              {tag.label || tag.id}
                            </Badge>)}
                        </div>
                      </div>}
                </div>
              </div>}

            {/* Visual Aids Section */}
            {visualCount > 0 && <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-6259" data-mp="3b1idx8y65tc3">
                <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6260" data-mp="3f0809r16ql7v">
                  <Image className="w-4 h-4 text-purple-400" data-id="element-6261" data-mp="318jq3tahy6tj" />
                  Visuals ({visualCount})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6262" data-mp="366bgujzenmw5">
                  {question.stimulusAids && question.stimulusAids.length > 0 && <div className="space-y-3" data-id="element-6263" data-mp="2j2td4g5okhk">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6264" data-mp="2knl6mb35pbq">
                          Stimulus Aids
                        </h5>
                        {question.stimulusAids.map((aid, idx) => <div key={`stim-${idx}`} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-6265" data-mp="7ugma8jd5qe5" data-mpi={idx}>
                    
                            <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-6266" data-mp="gol64802y1k6">
                              <code className="text-xs font-mono font-bold text-purple-700" data-id="element-6267" data-mp="3p56k637kjku5">
                                {aid.type}
                              </code>
                              <span className="text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-6268" data-mp="2xq4ngrwumqm">
                                {aid.showWhen || 'always'}
                              </span>
                            </div>
                            <div className="p-4 flex justify-center items-center min-h-[120px] bg-white" data-id="element-6269" data-mp="1eeg50j9nkkcb">
                              <div className="transform scale-75 origin-center w-full max-w-sm" data-id="element-6270" data-mp="1h6qd29ft36zu">
                                <VisualAidFactory visualAid={aid} mode="stimulus" data-id="element-6271" data-mp="1c7n6ulo3auqa" />
                        
                              </div>
                            </div>
                          </div>)}
                      </div>}

                  {question.supportAids && question.supportAids.length > 0 && <div className="space-y-3" data-id="element-6272" data-mp="12fzk2y2ha1v5">
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6273" data-mp="yh9xqfzgp9zd">
                        Support Aids
                      </h5>
                      {question.supportAids.map((aid, idx) => <div key={`supp-${idx}`} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-6274" data-mp="oj41adwfcw1w" data-mpi={idx}>
                    
                          <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-6275" data-mp="jlc8zirvb7x6">
                            <code className="text-xs font-mono font-bold text-amber-700" data-id="element-6276" data-mp="2dwh7y9s0ew66">
                              {aid.type}
                            </code>
                            <span className="text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-6277" data-mp="2mbwwrcsjg4pr">
                              {aid.showWhen || 'always'}
                            </span>
                          </div>
                          <div className="p-4 flex justify-center items-center min-h-[120px] bg-white" data-id="element-6278" data-mp="304cupz6l1cjt">
                            <div className="transform scale-75 origin-center w-full max-w-sm" data-id="element-6279" data-mp="23g6v5qoipace">
                              <VisualAidFactory visualAid={aid} mode="support" data-id="element-6280" data-mp="27hs4j21uf6hu" />
                        
                            </div>
                          </div>
                        </div>)}
                    </div>}
                </div>
              </div>}

            {/* Feedback Completeness */}
            <div className="p-4 sm:p-5 bg-gray-50/50" data-id="element-6281" data-mp="3ntxicvdokxzf">
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-6282" data-mp="3su3aecnv4mi1">
                <MessageSquare className="w-3.5 h-3.5" data-id="element-6283" data-mp="3354xv5wkhuzb" />
                Feedback Compleetheid
              </h5>
              <div className="flex flex-wrap gap-2" data-id="element-6284" data-mp="2y5tf5rcn4ff3">
                <Badge variant={question.feedback?.explanationText ? 'success' : 'danger'} size="sm" data-id="element-6285" data-mp="3d43wdvnmca8l">
                  
                  {question.feedback?.explanationText ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6286" data-mp="385k99rxagsen" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6287" data-mp="2kf6pspsrs1vo" />}
                  Uitleg tekst
                </Badge>
                <Badge variant={question.feedback?.solutionSteps?.length ? 'success' : 'warning'} size="sm" data-id="element-6288" data-mp="2fgve09iwzhat">
                  
                  {question.feedback?.solutionSteps?.length ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6289" data-mp="2pejbv8bth4x7" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6290" data-mp="2pdrh1zq1nb7z" />}
                  Stappenplan
                </Badge>
                <Badge variant={question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? 'success' : 'warning'} size="sm" data-id="element-6291" data-mp="21n45n4gx63go">
                  
                  {question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6292" data-mp="h0mkrnq4sgzf" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6293" data-mp="2af0r3kh6pwm" />}
                  Fout-specifieke feedback
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}